import { Button, Modal } from 'antd';
import useResize from 'hooks/useResize';
import React, { memo, useState } from 'react';
import styled from 'styled-components';
import FilesIcon from './copyIcon.svg';
import translations from 'translations';
import { Order } from 'types/schema';
import media from 'utils/mediaStyle';
import utilMessages from 'utils/messages';
import OrderCompleteIcon from '../icons/OrderCompleteIcon';
import SVGIcon from 'components/SVGIcon/SVGIcon';

const AModal = styled(Modal)`
  max-width: 343px !important;

  .ant-modal-title {
    text-align: center !important;
    font-size: 20px !important;
    font-weight: 500 !important;
    line-height: 26px !important;
    color: #2f382c !important;
  }
  .ant-modal-content {
    padding: 20px !important;
  }
  .ant-modal-body {
    width: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
  }

  ${media.md`
    width: 100% !important;
    max-width: 480px !important;
  `}
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  color: #111827;
  margin-top: 8px;
  text-align: center;

  ${media.md`
    margin-top: 16px;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
  `}
`;

const OrderIdTitle = styled.div`
  margin-top: 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #4b5563;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  ${media.md`
    flex-direction: row;
  `}
`;

const OrderIdRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const OrderId = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: #111827;
`;

const CopyIcon = styled.span<{ $copied: boolean }>`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  opacity: ${({ $copied }) => ($copied ? 0.4 : 1)};
  transition: opacity 0.3s ease;

  &:hover {
    opacity: ${({ $copied }) => ($copied ? 0.4 : 0.7)};
  }
`;

const ButtonActionsContainer = styled.div`
  width: 100%;
  margin-top: 24px;
  display: flex;
  justify-content: center;
  gap: 8px;

  ${media.md`
      margin-top: 40px;
      gap: 12px;
  `}
`;
const GoToHomeButton = styled(Button)`
  width: 100% !important;
  height: 44px;
  font-weight: 500 !important;
  font-size: 16px !important;
  box-shadow: none !important;
  color: #111827 !important;
`;

const ViewOrderDetailButton = styled(Button)`
  width: 100% !important;
  height: 44px;
  font-weight: 500 !important;
  font-size: 16px !important;
  box-shadow: none !important;
  background: #43a047 !important;
  color: #fff !important;
`;

interface OrderCompleteModalProps {
  isOpen: boolean;
  onGoToOrderDetail: (id: string) => void;
  onGoHome: () => void;
  onClose: () => void;
  isPendingApproval: boolean;
  orders: Order[];
  onTrackOrdersCompleted: (ordersId) => void;
}
const OrderCompleteModal = (props: OrderCompleteModalProps) => {
  const { isOpen, onGoToOrderDetail, onGoHome, onClose, isPendingApproval, orders, onTrackOrdersCompleted } = props;

  const { isMobile } = useResize();

  const [copied, setCopied] = useState(false);

  const handleCopyOrderId = () => {
    if (!orders?.length) return;

    navigator.clipboard.writeText(orders[0].id);

    setCopied(true);

    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <AModal
        destroyOnHidden={false}
        title={' '}
        closable={true}
        open={isOpen}
        footer={null}
        centered
        onCancel={onClose}
      >
        <OrderCompleteIcon width={isMobile ? 32 : 40} height={isMobile ? 32 : 40} />
        <Title>
          {/* {translations(utilMessages.congrats)} <br /> */}
          {isPendingApproval
            ? translations(utilMessages.yourPendingApprovalOrderSent)
            : translations(utilMessages.yourOrderCreated)}
        </Title>
        {!!orders?.length && (
          <OrderIdTitle>
            <span>{translations(utilMessages.orderId)}:</span>
            <OrderIdRow>
              <OrderId>#{orders[0].id}</OrderId>
              <CopyIcon $copied={copied} onClick={handleCopyOrderId}>
                <SVGIcon src={FilesIcon} />
              </CopyIcon>
            </OrderIdRow>
          </OrderIdTitle>
        )}
        <ButtonActionsContainer>
          <GoToHomeButton
            type="default"
            onClick={() => {
              onGoHome();
              onTrackOrdersCompleted(orders);
            }}
          >
            {translations(utilMessages.home)}
          </GoToHomeButton>
          {!!orders?.length && (
            <ViewOrderDetailButton
              type="primary"
              onClick={() => {
                onGoToOrderDetail(orders[0].id);
                onTrackOrdersCompleted(orders);
              }}
            >
              {translations(utilMessages.viewOrderDetail)}
            </ViewOrderDetailButton>
          )}
        </ButtonActionsContainer>
      </AModal>
    </>
  );
};

export default memo(OrderCompleteModal);
